import React, { useEffect, useMemo, useRef, useState } from "react";

import { useDispatch, useSelector } from "react-redux";
import { CreditCard as CreditCardIcon, Lock, Plus } from "lucide-react";

import CardSection from "../../common/StripeCardForm";
import DialogBox from "../Dashboard/cardPopup";
import AddCardForm from "../Dashboard/addCardForm";
import { Loader } from "../../common/loader";
import { Button } from "../../common/customDesignedComponents/button";
import { Input } from "../../common/customDesignedComponents/input";
import { TAILWIND_GRADIENT_SUBMIT_BUTTON_CLASS } from "../../helpers/constants";
import {
  findCountyLocationData,
  getMonthFromString,
  getObjLength,
  getStoredPromoCode,
  persistStoredPromoCode,
  clearStoredPromoCode,
} from "../../helpers/utils";
import { attachEnhancedToCompleteUpgradeFlags } from "../../helpers/generatePropertySecurityQuestions";
import { userSigupAction } from "../../../redux/slices/user/userSignUpSlice";
import { userProfileAction } from "../../../redux/slices/user/userProfileSlice";
import {
  hasAppliedPromoDiscount,
  isValidPaymentSummaryData,
  OrderSummaryBreakdown,
  investorPriceBreakdownToSummary,
} from "../../common/customDesignedComponents/OrderSummaryModal";
import VisaCard from "../../../assets/dashboard/visa.svg";
import MasterCard from "../../../assets/dashboard/mastercard.svg";
import DiscoverCard from "../../../assets/dashboard/discover.svg";
import AmericanCard from "../../../assets/dashboard/american-express.svg";
import { cardToSelect } from "./savedCardSelection";

type SavedCard = {
  id: number;
  brand?: string;
  last4?: string;
  exp_month?: string | number;
  exp_year?: number;
  default?: number;
};

type TailwindPropertyPaymentProps = {
  cardData: (cards: SavedCard[]) => void;
  addPromoCode?: boolean;
  promoCodeValue?: (code: string) => void;
  userId?: number | string | null;
  uid?: number | string | null;
  default_card_id?: number | string | null;
  selectedCategory?: string;
  selectedData?: Record<string, unknown>;
  propertiesIds?: Array<number | string>;
  stripeErr?: string;
  validateErr?: (event: unknown) => void;
  showInlineCardForm?: boolean;
  callListingAPI?: () => void;
  userEmail?: string;
  onInlineCardPay?: () => void | Promise<void>;
  inlinePayLoading?: boolean;
  inlinePayLabel?: string;
  isAdmin?: boolean;
  isServiceProvider?: boolean;
  isAdminCreateUser?: boolean;
  from?: string;
  isUserCreateProperty?: boolean;
  isUpgradeCategory?: boolean;
  isEnhancedToCompleteProtection?: boolean;
  finalAmount?: Record<string, unknown> | null;
  onFinalAmountChange?: (data: Record<string, unknown>) => void;
  is_renew?: boolean;
  /** Promo already applied to the price (County Investor default code). */
  defaultPromoCode?: string;
};

const isCardExpired = (card: SavedCard) => {
  if (!card?.id) return true;
  if ((card.exp_year ?? 0) > new Date().getFullYear()) return false;
  if ((card.exp_year ?? 0) < new Date().getFullYear()) return true;
  return (
    getMonthFromString(card.exp_month, card.exp_year) <
    new Date().getMonth() + 1
  );
};

const renderBrandIcon = (brand?: string) => {
  if (brand === "MasterCard") {
    return <img src={MasterCard} alt="MasterCard" className="h-7 w-auto" />;
  }
  if (brand === "Visa") {
    return <img src={VisaCard} alt="Visa" className="h-7 w-auto" />;
  }
  if (brand === "American Express") {
    return (
      <img src={AmericanCard} alt="American Express" className="h-7 w-auto" />
    );
  }
  if (brand === "Discover") {
    return <img src={DiscoverCard} alt="Discover" className="h-7 w-auto" />;
  }
  return <CreditCardIcon className="h-6 w-6 text-muted-foreground" />;
};

const TailwindPropertyPayment = ({
  cardData,
  addPromoCode = false,
  promoCodeValue = () => {},
  userId = null,
  uid = null,
  default_card_id = null,
  selectedCategory = "complete_protection",
  selectedData = {},
  propertiesIds,
  stripeErr = "",
  validateErr = () => {},
  showInlineCardForm = true,
  callListingAPI = () => {},
  userEmail,
  onInlineCardPay,
  inlinePayLoading = false,
  inlinePayLabel = "Pay with this card",
  isAdmin = false,
  isServiceProvider = false,
  isAdminCreateUser = false,
  from = "",
  isUserCreateProperty = false,
  isUpgradeCategory = false,
  isEnhancedToCompleteProtection = false,
  finalAmount = null,
  onFinalAmountChange = () => {},
  is_renew = false,
  defaultPromoCode = "",
}: TailwindPropertyPaymentProps) => {
  const dispatch = useDispatch();
  const [selectedCardId, setSelectedCardId] = useState<number | "">("");
  const [useNewCard, setUseNewCard] = useState(false);
  const [inlinePromoCode, setInlinePromoCode] = useState(
    () => getStoredPromoCode() || defaultPromoCode || "",
  );
  const [appliedPromoCode, setAppliedPromoCode] = useState(() => {
    const stored = getStoredPromoCode();
    if (stored) {
      return hasAppliedPromoDiscount(finalAmount as any) ? stored : "";
    }
    // Already priced in (get-final-amount accepted it), so it shows as applied.
    return defaultPromoCode || "";
  });
  const [promoMessage, setPromoMessage] = useState("");
  const [promoMessageIsError, setPromoMessageIsError] = useState(false);
  const [livePricingData, setLivePricingData] = useState(finalAmount);
  const [isAddCardModalOpen, setIsAddCardModalOpen] = useState(false);
  const [deferAddCardModal, setDeferAddCardModal] = useState(false);
  const promoApplyRef = useRef(false);
  const promoRemoveRef = useRef(false);
  const pendingApplyCodeRef = useRef("");
  const hydratedPromoRef = useRef(false);

  const notifyPromoCodeValue = (code: string) => {
    if (typeof promoCodeValue === "function") {
      promoCodeValue(code);
    }
  };

  // Client-scoped card APIs only for SP creating/paying for a client.
  // SP personal (/servicer-properties) uses service-provider/card/list|create.
  const useSpClientCardApi = Boolean(
    isServiceProvider && uid && isAdminCreateUser,
  );

  useEffect(() => {
    if (!deferAddCardModal) return undefined;
    const timer = window.setTimeout(() => {
      setIsAddCardModalOpen(true);
      setDeferAddCardModal(false);
    }, 0);
    return () => window.clearTimeout(timer);
  }, [deferAddCardModal]);

  const cardDetails = useSelector(
    (state: any) => state.userProfile.cardDetails,
  );
  const cardLoader = useSelector((state: any) => state.userProfile.cardLoader);
  const reqLoader = useSelector((state: any) => state.properties.reqLoader);
  const {
    totalPaymentDetails,
    getTotalAmountLoader,
    statesLoader,
    investorsID,
    investorsYearlyData,
  } = useSelector((state: any) => state.signUpUser);
  const { selectedPropertiesData, selectedProperty } = useSelector(
    (state: any) => state.properties,
  );
  const { investorReportResultData, invToken } = useSelector(
    (state: any) => state.propertyRiskManagement,
  );

  const savedCards: SavedCard[] = useMemo(
    () => (Array.isArray(cardDetails?.data) ? cardDetails.data : []),
    [cardDetails?.data],
  );
  const hasSavedCards = savedCards.length > 0;

  useEffect(() => {
    if (cardDetails?.success) return;
    if (callListingAPI && (useSpClientCardApi || (!isServiceProvider && uid))) {
      callListingAPI();
      return;
    }
    if (isAdmin) return;
    // County Investor page: the parent loads get-cards with the county token; the
    // customer list needs a login and its 401 would end the session.
    if (invToken) return;
    const listUrl = useSpClientCardApi
      ? `service-provider/customer/users/${uid}/card`
      : isServiceProvider
        ? "service-provider/card/list"
        : "customer/card/list";
    dispatch(
      userProfileAction.userCardDetails({
        url: listUrl,
        data: {},
        token: true,
      }),
    );
  }, [
    callListingAPI,
    cardDetails?.success,
    dispatch,
    invToken,
    isAdmin,
    isServiceProvider,
    uid,
    useSpClientCardApi,
  ]);

  const selectCard = (card: SavedCard) => {
    if (isCardExpired(card)) return;
    setUseNewCard(false);
    setSelectedCardId(card.id);
    cardData([card]);
  };

  useEffect(() => {
    const target = cardToSelect(savedCards, default_card_id);
    if (target) {
      setUseNewCard(false);
      setSelectedCardId(target.id);
      cardData([target]);
      return;
    }

    if (!hasSavedCards) {
      setUseNewCard(true);
      cardData([]);
    }
  }, [cardData, default_card_id, hasSavedCards, savedCards]);

  useEffect(() => {
    if (isValidPaymentSummaryData(finalAmount as any)) {
      setLivePricingData(finalAmount);
      const stored = getStoredPromoCode();
      if (stored && hasAppliedPromoDiscount(finalAmount as any)) {
        setInlinePromoCode(stored);
        setAppliedPromoCode(stored);
        notifyPromoCodeValue(stored);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps -- sync from Order Summary finalAmount + stored promo
  }, [finalAmount]);

  useEffect(() => {
    const stored = getStoredPromoCode();
    if (!stored) return;
    setInlinePromoCode((current) => current || stored);
    if (
      hasAppliedPromoDiscount(finalAmount as any) ||
      hasAppliedPromoDiscount(livePricingData as any)
    ) {
      setAppliedPromoCode(stored);
      notifyPromoCodeValue(stored);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps -- autofill once from Order Summary promo
  }, []);

  useEffect(() => {
    if (!getObjLength(totalPaymentDetails)) return;
    const responseData = investorPriceBreakdownToSummary(
      totalPaymentDetails?.data,
    );

    const promoErrorMessage =
      totalPaymentDetails?.data?.message ||
      totalPaymentDetails?.message ||
      "Unable to apply promo code.";

    if (promoRemoveRef.current) {
      if (
        totalPaymentDetails?.success &&
        isValidPaymentSummaryData(responseData)
      ) {
        promoRemoveRef.current = false;
        setPromoMessage("");
        setPromoMessageIsError(false);
        setAppliedPromoCode("");
        clearStoredPromoCode();
        notifyPromoCodeValue("");
        setLivePricingData(responseData);
        onFinalAmountChange(responseData);
        return;
      }
      promoRemoveRef.current = false;
      setPromoMessage(
        totalPaymentDetails?.data?.message ||
          totalPaymentDetails?.message ||
          "Unable to remove promo code.",
      );
      setPromoMessageIsError(true);
      return;
    }

    if (promoApplyRef.current) {
      promoApplyRef.current = false;
      const nextData = responseData;
      const code =
        pendingApplyCodeRef.current ||
        inlinePromoCode.trim() ||
        appliedPromoCode;
      const discountApplied =
        totalPaymentDetails?.success && hasAppliedPromoDiscount(nextData);

      // Accepted: keep the code for the payment. Only a real discount gets the
      // green note; a code that takes nothing off is applied quietly, so the
      // API's success text is never shown as an error.
      if (totalPaymentDetails?.success && isValidPaymentSummaryData(nextData)) {
        if (code) {
          persistStoredPromoCode(code);
          notifyPromoCodeValue(code);
          setAppliedPromoCode(code);
        }
        pendingApplyCodeRef.current = "";
        setPromoMessage(discountApplied ? "Promo code applied." : "");
        setPromoMessageIsError(false);
        setLivePricingData(nextData);
        onFinalAmountChange(nextData);
        return;
      }

      clearStoredPromoCode();
      notifyPromoCodeValue("");
      setAppliedPromoCode("");
      pendingApplyCodeRef.current = "";
      setPromoMessage(promoErrorMessage);
      setPromoMessageIsError(true);
      return;
    }

    // Hydrate / sync payment-screen pricing (including Order Summary promo re-fetch)
    if (
      totalPaymentDetails?.success &&
      isValidPaymentSummaryData(responseData)
    ) {
      const carried =
        getStoredPromoCode() ||
        pendingApplyCodeRef.current ||
        inlinePromoCode.trim();
      const responseHasDiscount = hasAppliedPromoDiscount(responseData);

      // If we carried a promo but the response has no discount, retry once with promo.
      if (carried && !responseHasDiscount && !promoRemoveRef.current) {
        if (!pendingApplyCodeRef.current) {
          pendingApplyCodeRef.current = carried;
          promoApplyRef.current = true;
          persistStoredPromoCode(carried);
          const { url, data } = buildFinalAmountPayload(carried);
          try {
            localStorage.setItem("Plan_Data", JSON.stringify(data));
          } catch {
            // ignore
          }
          dispatch(userSigupAction.getTotalPaymentDetails({ url, data }));
          return;
        }
        // Already retried — clear guard and show the response as-is.
        pendingApplyCodeRef.current = "";
      }

      setLivePricingData(responseData);
      onFinalAmountChange(responseData);
      if (carried && responseHasDiscount) {
        setInlinePromoCode(carried);
        setAppliedPromoCode(carried);
        notifyPromoCodeValue(carried);
        pendingApplyCodeRef.current = "";
      }
      return;
    }

    if (addPromoCode && !totalPaymentDetails?.success) {
      setPromoMessage(promoErrorMessage);
      setPromoMessageIsError(true);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps -- only react to payment details response
  }, [totalPaymentDetails]);

  useEffect(() => {
    return () => {
      dispatch(userSigupAction.fetchTotalPaymentDetails({}));
    };
  }, [dispatch]);

  const buildFinalAmountPayload = (promoCode: string | null) => {
    let planData: Record<string, unknown> = {};
    try {
      planData = JSON.parse(localStorage.getItem("Plan_Data") || "{}");
    } catch {
      planData = {};
    }
    let data: Record<string, unknown> = { ...planData };
    let url = "customer/get-final-amount";
    if (selectedCategory === "enhanced_monitoring") {
      url = "get-final-amount-enhanced-monitoring";
    }
    const resolvedPromo = promoCode || getStoredPromoCode() || null;
    if (resolvedPromo) {
      data.promo_code = resolvedPromo;
    } else {
      delete data.promo_code;
    }
    if (selectedCategory !== "enhanced_monitoring") {
      data = attachEnhancedToCompleteUpgradeFlags(data, {
        property: selectedProperty || selectedData,
        forceUpgrade: Boolean(isEnhancedToCompleteProtection),
      }) as Record<string, unknown>;
    }
    // County Investor page: get-final-amount, the pricing investor-payment charges
    // (and it rejects an invalid promo); investor pages keep their own endpoint.
    const isCountySitePayment = Boolean(
      invToken &&
      findCountyLocationData(window.location.pathname.split("/")[1] || ""),
    );
    if (isCountySitePayment) {
      url = "get-final-amount";
      data = {
        user_id: userId || investorReportResultData?.data?.user?.id,
        plan_id: investorsYearlyData?.plan_id,
        property_id: propertiesIds,
      };
      // An empty promo_code counts as an invalid code: leave it out.
      if (resolvedPromo) {
        data.promo_code = resolvedPromo;
      }
    } else if (investorsID || invToken) {
      url = "investors-price-details";
      data = {
        user_id: userId || investorReportResultData?.data?.user?.id,
        property_id: selectedPropertiesData?.properties_Id ||
          propertiesIds || [selectedProperty?.property_id],
        plan_id: investorsYearlyData?.plan_id,
      };
      if (resolvedPromo) {
        data.promo_code = resolvedPromo;
      }
      if (invToken) {
        delete data.property_ids;
      }
    }
    return { url, data };
  };

  useEffect(() => {
    const shouldHydratePricing =
      Boolean(isUpgradeCategory) ||
      Boolean(isEnhancedToCompleteProtection) ||
      Boolean(isUserCreateProperty) ||
      from === "add-subscriptions" ||
      from === "add-property";
    if (!shouldHydratePricing) return;
    if (is_renew) return;
    if (promoApplyRef.current || promoRemoveRef.current) return;
    if (hydratedPromoRef.current) return;

    let planData: Record<string, unknown> = {};
    try {
      planData = JSON.parse(localStorage.getItem("Plan_Data") || "{}");
    } catch {
      planData = {};
    }
    if (!getObjLength(planData)) return;

    // Prefer live field / state over storage — storage can be cleared between
    // useState init (which autofills the input) and this effect.
    const carriedPromo =
      getStoredPromoCode() ||
      (typeof sessionStorage !== "undefined"
        ? sessionStorage.getItem("ep_carried_promo") || ""
        : "") ||
      inlinePromoCode.trim() ||
      "";

    const hasValidPricing =
      isValidPaymentSummaryData(livePricingData as any) ||
      isValidPaymentSummaryData(finalAmount as any);

    // Skip only when pricing is already present and there is no promo to apply.
    if (hasValidPricing && !carriedPromo) {
      return;
    }

    hydratedPromoRef.current = true;
    if (carriedPromo) {
      persistStoredPromoCode(carriedPromo);
      try {
        sessionStorage.setItem("ep_carried_promo", carriedPromo);
      } catch {
        // ignore
      }
      setInlinePromoCode(carriedPromo);
      notifyPromoCodeValue(carriedPromo);
    }

    const { url, data } = buildFinalAmountPayload(carriedPromo || null);
    try {
      localStorage.setItem("Plan_Data", JSON.stringify(data));
    } catch {
      // ignore
    }
    dispatch(userSigupAction.getTotalPaymentDetails({ url, data }));
    // eslint-disable-next-line react-hooks/exhaustive-deps -- hydrate once on mount when pricing/promo needs sync
  }, [
    from,
    isUserCreateProperty,
    isUpgradeCategory,
    isEnhancedToCompleteProtection,
  ]);

  const applyPromoCode = () => {
    const code = inlinePromoCode.trim();
    if (!code) return;

    promoApplyRef.current = true;
    promoRemoveRef.current = false;
    pendingApplyCodeRef.current = code;
    setPromoMessage("");
    setPromoMessageIsError(false);

    const { url, data } = buildFinalAmountPayload(code);
    dispatch(
      userSigupAction.getTotalPaymentDetails({
        url,
        data,
      }),
    );
  };

  const removePromoCode = () => {
    promoRemoveRef.current = true;
    promoApplyRef.current = false;
    pendingApplyCodeRef.current = "";
    clearStoredPromoCode();
    setInlinePromoCode("");
    setAppliedPromoCode("");
    setPromoMessage("");
    setPromoMessageIsError(false);
    notifyPromoCodeValue("");

    const { url, data } = buildFinalAmountPayload(null);
    try {
      localStorage.setItem("Plan_Data", JSON.stringify(data));
    } catch {
      // ignore
    }
    dispatch(
      userSigupAction.getTotalPaymentDetails({
        url,
        data,
      }),
    );
  };

  const refreshCards = () => {
    // Prefer parent listing callback so admin/SP-client use the correct URL.
    // Never also dispatch customer/card/list — takeEvery races overwrite Redux.
    if (typeof callListingAPI === "function") {
      callListingAPI();
      return;
    }
    if (useSpClientCardApi) {
      dispatch(
        userProfileAction.userCardDetails({
          url: `service-provider/customer/users/${uid}/card`,
          data: {},
          token: true,
        }),
      );
      return;
    }
    if (isAdmin && uid) {
      dispatch(
        userProfileAction.userCardDetails({
          url: `admin/users/${uid}/card`,
          data: {},
          token: true,
        }),
      );
      return;
    }
    dispatch(
      userProfileAction.userCardDetails({
        url: isServiceProvider
          ? "service-provider/card/list"
          : "customer/card/list",
        data: {},
        token: true,
      }),
    );
  };

  const totalPaymentSummary = investorPriceBreakdownToSummary(
    totalPaymentDetails?.data,
  );
  const totalDueToday =
    totalPaymentDetails?.success && totalPaymentSummary?.total != null
      ? Number(totalPaymentSummary.total).toFixed(2)
      : null;

  const pricingSummaryData = isValidPaymentSummaryData(livePricingData as any)
    ? livePricingData
    : isValidPaymentSummaryData(finalAmount as any)
      ? finalAmount
      : totalPaymentDetails?.success &&
          isValidPaymentSummaryData(totalPaymentSummary)
        ? totalPaymentSummary
        : livePricingData && getObjLength(livePricingData)
          ? livePricingData
          : finalAmount && getObjLength(finalAmount)
            ? finalAmount
            : null;
  // Admin Add Property/Subscription and any Upgrade (customer or admin) payment
  // Add Property / Add Subscription / Upgrade (customer or admin)
  // County Investor page payment (token user, no login): same breakdown + promo.
  const showAdminPricingBreakdown =
    Boolean(invToken) ||
    Boolean(isUpgradeCategory) ||
    Boolean(isEnhancedToCompleteProtection) ||
    Boolean(isUserCreateProperty) ||
    Boolean(is_renew) ||
    from === "add-subscriptions" ||
    from === "add-property";
  const promoIsActive =
    hasAppliedPromoDiscount(pricingSummaryData as any) ||
    Boolean(appliedPromoCode);

  return (
    <div className="tailwind_component w-full min-w-0 pb-2 sm:px-4 sm:pb-3">
      <div className="mb-4 sm:mb-6 w-full min-w-0 sm:px-3">
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 mb-4">
          <h2 className="text-lg font-medium text-foreground">
            Payment information
          </h2>
          <Button
            type="button"
            variant="outline"
            size="sm"
            className="text-secondary border-secondary hover:bg-secondary/10 shrink-0"
            onClick={() => setDeferAddCardModal(true)}
          >
            <Plus className="h-4 w-4 mr-1.5" />
            Add new card
          </Button>
        </div>

        {cardLoader && !hasSavedCards ? (
          <p className="text-sm text-muted-foreground">Loading saved cards…</p>
        ) : null}

        {hasSavedCards ? (
          <div className="space-y-3 mb-4">
            {savedCards.map((card) => {
              const expired = isCardExpired(card);
              const isSelected = !useNewCard && selectedCardId === card.id;
              const isDefault = default_card_id
                ? card.id === parseInt(String(default_card_id), 10)
                : card.default === 1;

              return (
                <button
                  key={card.id}
                  type="button"
                  disabled={expired}
                  onClick={() => selectCard(card)}
                  className={`w-full min-w-0 text-left p-2.5 sm:p-4 rounded-xl border transition-colors ${
                    expired
                      ? "opacity-50 cursor-not-allowed bg-muted/20 border-border/40"
                      : isSelected
                        ? "bg-secondary/5 border-secondary ring-1 ring-secondary/30"
                        : "bg-muted/30 border-border/50 hover:border-secondary/40"
                  }`}
                >
                  <div className="flex items-start gap-3 min-w-0">
                    <span
                      className={`mt-0.5 h-4 w-4 rounded-full border-2 shrink-0 flex items-center justify-center ${
                        isSelected
                          ? "border-secondary"
                          : "border-muted-foreground/40"
                      }`}
                    >
                      {isSelected ? (
                        <span className="h-2 w-2 rounded-full bg-secondary" />
                      ) : null}
                    </span>
                    <div className="flex-1 min-w-0 space-y-2">
                      <div className="flex items-center gap-2 min-w-0">
                        <div className="w-10 h-8 sm:w-12 sm:h-10 rounded flex items-center justify-center shrink-0">
                          {renderBrandIcon(card.brand)}
                        </div>
                        <span className="text-foreground uppercase text-sm font-medium break-words [word-break:normal]">
                          {card.brand} - **** {card.last4}
                        </span>
                      </div>
                      <div className="flex flex-wrap items-center gap-x-2 gap-y-1">
                        {isDefault ? (
                          <span className="px-2 py-0.5 bg-emerald-100 text-emerald-800 text-xs rounded-full font-medium whitespace-nowrap shrink-0">
                            DEFAULT
                          </span>
                        ) : null}
                        {expired ? (
                          <span className="px-2 py-0.5 bg-destructive/10 text-destructive text-xs rounded-full font-medium whitespace-nowrap shrink-0">
                            EXPIRED
                          </span>
                        ) : null}
                        <span className="text-muted-foreground text-xs sm:text-sm whitespace-nowrap">
                          Exp. {card.exp_month} {card.exp_year}
                        </span>
                      </div>
                    </div>
                  </div>
                </button>
              );
            })}
          </div>
        ) : (
          <p className="text-sm text-muted-foreground mb-4">
            No saved cards yet.
            {/* Enter your card details below or */} Add a card to your account.
          </p>
        )}

        {showAdminPricingBreakdown ? (
          <OrderSummaryBreakdown
            paymentData={pricingSummaryData as any}
            category={selectedCategory}
            className="mb-3 sm:mb-4"
            showPromoCode={!is_renew}
            promoCodeValue={inlinePromoCode}
            onPromoCodeChange={setInlinePromoCode}
            onApplyPromoCode={applyPromoCode}
            onRemovePromoCode={removePromoCode}
            showRemovePromoCode={promoIsActive}
            promoApplying={Boolean(getTotalAmountLoader)}
            promoMessage={promoMessage}
            promoMessageIsError={promoMessageIsError}
          />
        ) : null}

        {/*     {hasSavedCards && showInlineCardForm ? (
          <button
            type="button"
            onClick={() => {
              setUseNewCard(true);
              setSelectedCardId("");
              cardData([]);
            }}
            className={`w-full text-left p-4 rounded-xl border transition-colors mb-4 ${
              useNewCard
                ? "bg-secondary/5 border-secondary ring-1 ring-secondary/30"
                : "border-dashed border-border hover:border-secondary/40"
            }`}
          >
            <span className="text-sm font-medium text-foreground">
              Pay with a different card
            </span>
          </button>
        ) : null}

      {showInlineCardForm &&
        !isAddCardModalOpen &&
        !deferAddCardModal &&
        (useNewCard || !hasSavedCards) ? (
          <div className="rounded-xl border border-border bg-muted/20 p-4 mb-4 space-y-4">
            <CardSection
              stripeErr={stripeErr}
              validateErr={validateErr}
              variant="modern"
            />
            {onInlineCardPay ? (
              <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 pt-1">
                <p className="text-xs text-muted-foreground">
                  This card is used for this payment only and is not saved to
                  your account.
                </p>
                <Button
                  type="button"
                  onClick={() => onInlineCardPay()}
                  disabled={inlinePayLoading}
                  className={`${TAILWIND_GRADIENT_SUBMIT_BUTTON_CLASS} shrink-0 px-8`}
                >
                  {inlinePayLoading ? "Processing…" : inlinePayLabel}
                </Button>
              </div>
            ) : null}
          </div>
        ) : null} */}

        {!showAdminPricingBreakdown && addPromoCode ? (
          <div className="rounded-xl border border-border bg-card p-2.5 sm:p-4 mb-3 sm:mb-4 w-full min-w-0">
            <label className="text-sm font-medium text-foreground mb-2 block">
              Promo code
            </label>
            <div className="flex flex-col sm:flex-row gap-2 w-full min-w-0">
              <Input
                value={inlinePromoCode}
                placeholder="Enter promo code"
                onChange={(event) => setInlinePromoCode(event.target.value)}
                className="flex-1 min-w-0 w-full"
              />
              <Button
                type="button"
                variant="default"
                size="sm"
                className="w-full sm:w-auto shrink-0 bg-gradient-to-r from-secondary to-cyan-400 hover:from-secondary/90 hover:to-cyan-400/90"
                onClick={applyPromoCode}
                disabled={
                  !inlinePromoCode.trim() || Boolean(getTotalAmountLoader)
                }
              >
                {getTotalAmountLoader ? "Applying…" : "Apply"}
              </Button>
            </div>
            {appliedPromoCode || promoMessage ? (
              <p
                className={`text-sm ${promoMessageIsError ? "text-destructive" : "text-emerald-700"} mt-2`}
              >
                {appliedPromoCode ? `Applied: ${appliedPromoCode}` : null}
                {promoMessage
                  ? `${appliedPromoCode ? " — " : ""}${promoMessage}`
                  : ""}
              </p>
            ) : null}
          </div>
        ) : null}

        {!showAdminPricingBreakdown && totalDueToday != null ? (
          <div className="flex items-center justify-between rounded-xl bg-secondary/5 border border-secondary/20 px-4 py-3">
            <span className="text-sm font-medium text-foreground">
              Total due today
            </span>
            <span className="text-lg font-semibold text-secondary">
              ${totalDueToday}
            </span>
          </div>
        ) : null}
      </div>

      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 pt-2 border-t border-border">
        <div className="flex items-center gap-2 text-xs text-muted-foreground">
          <Lock className="h-3.5 w-3.5 shrink-0" />
          <span>
            Secure payments powered by{" "}
            <span className="font-semibold" style={{ color: "#635BFF" }}>
              Stripe
            </span>
          </span>
        </div>
        <div className="flex items-center gap-2">
          <span className="px-2 py-1 rounded text-[10px] font-bold text-white bg-[#EB001B]">
            MC
          </span>
          <span className="px-2 py-1 rounded text-[10px] font-bold text-white bg-[#1A1F71]">
            VISA
          </span>
          <span className="px-2 py-1 rounded text-[10px] font-bold text-white bg-[#006FCF]">
            AMEX
          </span>
        </div>
      </div>

      {isAddCardModalOpen ? (
        <DialogBox
          width="sm"
          openPopup={isAddCardModalOpen}
          setOpenPopup={setIsAddCardModalOpen}
          //  onClose={() => setIsAddCardModalOpen(false)}
          modalOverApex
        >
          <AddCardForm
            openPopup={isAddCardModalOpen}
            isTailwindComponent
            fromAdmin={isAdmin}
            isServiceProvider={isServiceProvider}
            uid={isAdmin || useSpClientCardApi ? uid : null}
            handleCloseAddCardModal={() => {
              setIsAddCardModalOpen(false);
              refreshCards();
            }}
            onClose={() => {
              setIsAddCardModalOpen(false);
            }}
            callListingAPI={refreshCards}
          />
        </DialogBox>
      ) : null}

      <Loader open={reqLoader || statesLoader || cardLoader} />
    </div>
  );
};

export default TailwindPropertyPayment;
